import { FormStore, GlobalStore, GridStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { CreditPullTiersService } from '../services/creditPullTiersService';
import { CreditPullTierSettings } from '../types/creditPullTiersTypes';
import { TierAssignmentStore } from './tierAssignmentStore';

export type { CreditPullTierSettings };
export { TierAssignmentStore };

const tierForm = {
  fields: {
    includedPulls: {
      value: '',
      error: null,
      rule: 'required',
      label: 'Included Pulls Per Month',
      message: 'Included Pulls Per Month is required.',
    },
    unitPrice: {
      value: '',
      error: null,
      rule: 'required',
      label: 'Overage: Pricing Per Unit',
      message: 'Overage: Pricing Per Unit is required.',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class CreditPullTiersSettingsStore {
  globalStore: GlobalStore;
  private creditPullTiersService: CreditPullTiersService;

  tiers: CreditPullTierSettings[] = [];
  tierFormStores: FormStore[] = [];
  tierAssignmentGridStore: GridStore;
  tierAssignmentStore: TierAssignmentStore;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.creditPullTiersService = new CreditPullTiersService();
    this.tierAssignmentGridStore = new GridStore(() => this.fetchLendersTierAssignment());
    this.tierAssignmentStore = new TierAssignmentStore(globalStore, () => this.tierAssignmentGridStore.fetchGridData());

    makeObservable(this, {
      tiers: observable,
      tierFormStores: observable,
      fetchAllTiers: flow,
      bulkUpdateTiers: flow,
      reset: action,
    });
  }

  *fetchAllTiers() {
    try {
      const response = yield this.creditPullTiersService.getAllTiers();
      this.tiers = response.data?.data ?? [];
      this.tierFormStores = this.tiers.map(tier => {
        const formStore = new FormStore(tierForm, this.globalStore);
        formStore.loadForm({ includedPulls: tier.includedPulls, unitPrice: tier.unitPrice });
        return formStore;
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load credit pull tiers.',
      });
    }
  }

  *bulkUpdateTiers(): Generator<any, boolean, any> {
    try {
      const payload = this.tiers.map((tier, index) => {
        const values = this.tierFormStores[index].getFormValues();
        return {
          tier: tier.tier,
          includedPulls: Number(values.includedPulls),
          unitPrice: Number(values.unitPrice),
        };
      });
      const response = yield this.creditPullTiersService.bulkUpdateTiers(payload);
      this.tiers = response.data?.data ?? this.tiers;
      this.tierFormStores = this.tiers.map(tier => {
        const formStore = new FormStore(tierForm, this.globalStore);
        formStore.loadForm({ includedPulls: tier.includedPulls, unitPrice: tier.unitPrice });
        return formStore;
      });
      return true;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to update credit pull tiers.',
      });
      return false;
    }
  }

  fetchLendersTierAssignment = async () => {
    try {
      const { pageNumber, pageSize, sortBy, sortDir, filters } = this.tierAssignmentGridStore.gridData.meta;
      const response = await this.creditPullTiersService.getLendersTierAssignment(pageNumber, pageSize, sortBy, sortDir, filters);
      return response;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load lender tier assignments.',
      });
    }
  };

  reset() {
    this.tiers = [];
    this.tierFormStores = [];
  }
}
